/**
 * PageShell.tsx -- Top-level layout wrapper for every page.
 *
 * Renders (in order):
 *   1. Skip-to-content link (<a href="#main">) as the first focusable element
 *   2. Optional <AnnouncementBar /> (showAnnouncementBar, default true)
 *   3. <TopNav />
 *   4. <main id="main"> with optional gutter container (max-width 1280px)
 *   5. Optional <Footer /> (showFooter, default false)
 *
 * All colour, spacing, and focus-ring values reference CSS custom properties
 * from tokens.css -- zero hardcoded design values.
 *
 * @see specs/integration-plan.md Section 4.1
 * @see specs/prd.md FR-006
 */
import React from "react";

import AnnouncementBar from "./AnnouncementBar";
// TopNav exports as a named binding (`export function TopNav() { ... }`),
// not a default — the previous `import TopNav from "./TopNav"` tripped a
// MISSING_EXPORT build error during merge with origin/main.
import { TopNav } from "./TopNav";
import Footer from "./Footer";

export interface PageShellProps {
  children: React.ReactNode;
  /** Wrap main content in a max-width 1280px centred container. Default true. */
  gutter?: boolean;
  /** Render the Footer component. Default false (operator pages). */
  showFooter?: boolean;
  /** Render the AnnouncementBar component. Default true. */
  showAnnouncementBar?: boolean;
  /** Additional class name forwarded to the root element. */
  className?: string;
}

/**
 * CSS-in-JS style objects using only CSS custom property references.
 * Kept inline so the component is self-contained without a CSS module.
 */
const gutterStyle: React.CSSProperties = {
  maxWidth: "1280px",
  marginLeft: "auto",
  marginRight: "auto",
  paddingLeft: "var(--space-xl)",
  paddingRight: "var(--space-xl)",
};

export default function PageShell({
  children,
  gutter = true,
  showFooter = false,
  showAnnouncementBar = true,
  className,
}: PageShellProps) {
  return (
    <div className={className ?? undefined}>
      {/* Skip-to-content link -- MUST be the first focusable element */}
      <a href="#main" className="skip-link">
        Skip to main content
      </a>

      {showAnnouncementBar && <AnnouncementBar />}

      <TopNav />

      <main id="main">
        {gutter ? <div style={gutterStyle}>{children}</div> : children}
      </main>

      {showFooter && <Footer />}
    </div>
  );
}

export { PageShell };
